import { useTranslation } from "react-i18next";
import { useOnboardingWizard } from "../OnboardingWizardProvider";
import {
  ArrowRight,
  ArrowLeft,
  Mail,
  FileCheck,
  Sparkles,
  Smartphone,
  Mic,
  Camera,
} from "lucide-react";

const iconMap: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  Mail,
  FileCheck,
  Sparkles,
  Smartphone,
  Mic,
  Camera,
};

export function InstructionStep() {
  const { t } = useTranslation();
  const {
    stepConfig,
    goNext,
    goBack,
    isFirstStep,
    markStepComplete,
    currentStep,
    onStepComplete,
    executeAction,
    formData,
  } = useOnboardingWizard();

  const handleNext = async () => {
    markStepComplete(currentStep);
    onStepComplete(currentStep);
    const result = await executeAction({ type: "completeStep" }, { formData });
    if (!result.serverAdvanced) goNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          {t(stepConfig.title)}
        </h2>
        {stepConfig.subtitle && (
          <p className="text-muted-foreground">{t(stepConfig.subtitle)}</p>
        )}
      </div>

      {/* Instruction items */}
      {stepConfig.instructions && (
        <div className="space-y-4">
          {stepConfig.instructions.map((item, i) => {
            const Icon = iconMap[item.icon] || Sparkles;
            return (
              <div
                key={i}
                className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-nara-orange/15 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-nara-orange" />
                </div>
                <p className="text-foreground text-sm leading-relaxed pt-2">
                  {t(item.text)}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        {!isFirstStep && stepConfig.backAllowed !== false && (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 px-4 py-3 text-foreground hover:bg-accent rounded-xl transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common.back")}
          </button>
        )}
        <button
          onClick={handleNext}
          className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-nara-orange text-nara-charcoal font-semibold rounded-xl hover:bg-nara-orange/90 transition-colors min-h-[48px]"
        >
          {t(stepConfig.nextLabel || "common.next")}
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
